/* eslint-disable no-nested-ternary */
import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  AlertTitle,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'

ValidationStep.propTypes = {
  identifiers: PropTypes.array.isRequired,
  validationResult: PropTypes.object,
  identifierLabel: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
}

export default function ValidationStep({
  identifiers,
  validationResult,
  identifierLabel,
  onBack,
  onNext,
}) {
  const { t } = useTranslation()

  if (!validationResult) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1">
          {t(
            'components.multi-document-import-dialog.validating',
            'Validating...'
          )}
        </Typography>
      </Box>
    )
  }

  const { valid = [], invalid = [], details = [] } = validationResult
  const hasInvalid = invalid.length > 0
  const hasValid = valid.length > 0
  const totalCount = identifiers.length

  return (
    <Box>
      {/* Summary Alert */}
      <Alert
        severity={!hasValid ? 'error' : hasInvalid ? 'warning' : 'success'}
        sx={{ mb: 2 }}
      >
        <AlertTitle>
          {!hasValid
            ? t(
                'components.multi-document-import-dialog.validation-failed',
                'Validation Failed'
              )
            : hasInvalid
            ? t(
                'components.multi-document-import-dialog.validation-partial',
                'Partial Success'
              )
            : t(
                'components.multi-document-import-dialog.validation-success',
                'Validation Successful'
              )}
        </AlertTitle>
        <Typography variant="body2">
          {t('components.multi-document-import-dialog.validation-summary', {
            total: totalCount,
            valid: valid.length,
            invalid: invalid.length,
            defaultValue: `{{valid}} of {{total}} file(s) validated successfully. {{invalid}} failed.`,
          })}
        </Typography>
      </Alert>

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        {t(
          'components.multi-document-import-dialog.validation-details',
          'Validation Details'
        )}
      </Typography>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width="20%">{identifierLabel}</TableCell>
              <TableCell width="30%">
                {t('common.filename', 'Filename')}
              </TableCell>
              <TableCell width="15%" align="center">
                {t('common.status', 'Status')}
              </TableCell>
              <TableCell width="35%">
                {t('common.details', 'Details')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {identifiers.map((identifier, index) => {
              const isValid = valid.includes(identifier.value)
              const isInvalid = invalid.includes(identifier.value)
              const detail = details.find(
                (d) => d.identifier === identifier.value
              )

              return (
                <TableRow
                  key={index}
                  sx={{
                    backgroundColor: isValid
                      ? '#e8f5e9'
                      : isInvalid
                      ? '#ffebee'
                      : 'inherit',
                  }}
                >
                  <TableCell>
                    <Chip
                      label={identifier.value || t('common.unknown', 'Unknown')}
                      size="small"
                      color={isValid ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: 'break-word' }}
                    >
                      {identifier.filename}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {isValid ? (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label={t('common.valid', 'Valid')}
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Chip
                        icon={<ErrorIcon />}
                        label={t('common.invalid', 'Invalid')}
                        color="error"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={isValid ? 'success.main' : 'error.main'}
                    >
                      {detail?.message ||
                        (isValid
                          ? t('common.found', 'Found')
                          : t('common.not-found', 'Not found'))}
                    </Typography>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          mt: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button variant="outlined" onClick={onBack}>
          {t('common.back', 'Back')}
        </Button>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
          }}
        >
          {!hasValid && (
            <Typography variant="caption" color="error" sx={{ mb: 1 }}>
              {t(
                'components.multi-document-import-dialog.no-valid-files',
                'No valid files to upload. Please go back and fix the errors.'
              )}
            </Typography>
          )}
          {hasValid && hasInvalid && (
            <Typography variant="caption" color="warning.main" sx={{ mb: 1 }}>
              {t(
                'components.multi-document-import-dialog.invalid-files-skipped',
                'Invalid files will be skipped and not uploaded'
              )}
            </Typography>
          )}
          <Button variant="contained" onClick={onNext} disabled={!hasValid}>
            {t('common.continue', 'Continue')}
            {hasValid &&
              ` (${valid.length} ${
                valid.length === 1
                  ? t('common.file', 'file')
                  : t('common.files', 'files')
              })`}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
